export type ProjectRef = {
  id?: string;
  project_name?: string | null;
  project_number?: string | null;
};

export type ProjectLineRow = {
  project?: ProjectRef | null;
  project_id?: string | null;
  project_name?: string | null;
  supplierInquiryLine?: {
    project?: ProjectRef | null;
    project_id?: string | null;
    project_name?: string | null;
  } | null;
};

function formatProjectRef(proj?: ProjectRef | null, fallbackName?: string | null): string | null {
  const number = (proj?.project_number ?? '').trim() || null;
  const name = (proj?.project_name ?? fallbackName ?? '').trim() || null;
  if (number && name) return `${number} - ${name}`;
  if (name) return name;
  if (number) return number;
  return null;
}

export function projectLabel(row?: ProjectLineRow | null): string {
  if (!row) return '—';
  const proj = row.project ?? row.supplierInquiryLine?.project ?? null;
  const formatted = formatProjectRef(
    proj,
    row.project_name ?? row.supplierInquiryLine?.project_name ?? null
  );
  if (formatted) return formatted;
  const id = row.project_id ?? row.supplierInquiryLine?.project_id;
  return id ? String(id) : '—';
}

/** Distinct project labels from line rows (sorted for stable display). */
export function collectDistinctProjectLabels(lines?: ProjectLineRow[] | null): string[] {
  if (!lines?.length) return [];
  const names = new Set<string>();
  for (const line of lines) {
    const label = projectLabel(line);
    if (label !== '—') names.add(label);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

export function hasMultipleLineProjects(lines?: ProjectLineRow[] | null): boolean {
  return collectDistinctProjectLabels(lines).length > 1;
}

/** At least one line has its own project (combined purchase requests). */
export function linesCarryProjects(lines?: ProjectLineRow[] | null): boolean {
  return collectDistinctProjectLabels(lines).length > 0;
}

export function headerProjectLabel(
  doc?: { project?: ProjectRef | null; project_id?: string | null; project_name?: string | null } | null,
  lines?: ProjectLineRow[] | null
): string {
  const headerLabel = formatProjectRef(doc?.project, doc?.project_name);
  if (headerLabel) return headerLabel;
  if (!lines?.length) return doc?.project_id ? String(doc.project_id) : '—';

  const labels = collectDistinctProjectLabels(lines);
  if (labels.length === 0) return doc?.project_id ? String(doc.project_id) : '—';
  if (labels.length === 1) return labels[0];
  return labels.join(', ');
}
